import { useEffect, useState, useRef } from "react";
import { POLLING_INTERVAL, MAX_RETRY_COUNT } from "../constants/constants";
import { checkIdVerifyStatus, triggerValidateIdentity } from "../api/identity";
import { idValidationUrl } from "../config";
import { useStatusMessage } from "../contexts/StatusMessageContext";
import { useApiActivity } from "../contexts/ApiActivityContext";
import type { UserInfo } from "../types/user";

/**
 * This hook is used when "Validate Identity" is selected. It triggers a series of API calls to verify a user's identity.
 *
 * Logic:
 *   If step = 0: Do nothing. Initial state.
 *   If step = 1: POST /trigger_id_validation.
 *   If step = 2: POST /get_challenge_value every POLLING_INTERVAL until user is validated or reaches MAX_RETRY_COUNT.
 *   If step = 9: Error occurred.
 */
const useValidateIdentity = (
  identityStep: number,
  setIdentityStep: (step: number) => void,
  userInfo: UserInfo
): void => {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const activityActiveRef = useRef(false);
  const { setStatus } = useStatusMessage();
  const { beginActivity, endActivity } = useApiActivity();

  const finishActivity = () => {
    if (activityActiveRef.current) {
      activityActiveRef.current = false;
      endActivity("identity");
    }
  };

  const stopPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  useEffect(() => {
    const validateIdentity = async () => {
      try {
        if (identityStep === 1) {
          setStatus({
            title: "Identity Validation in Progress ",
            message: `<span style="color: red;">Don't close this window</span><br>
                        Starting the identity validation session...`,
            isError: false,
            isOkButtonVisible: false,
          });

          const response = await triggerValidateIdentity((userInfo as any).okta?.id);

          if (response.error) {
            setStatus({
              message: `Error starting identity validation: ${response.error}`,
              isError: true,
              isOkButtonVisible: true,
            });
            setIdentityStep(9);
            finishActivity();
          } else if ((response?.data as any)?.session_id) {
            const sid = (response.data as any).session_id;
            setSessionId(sid);
            const message = `<span style="color: red;">Don't close this window</span><br>
                            Please advise ${(userInfo as any).firstName} to go to this website to complete identity validation:<br>
                            <b>${idValidationUrl}</b>`;
            setStatus({ message, isError: false, isOkButtonVisible: false });
            setIdentityStep(2);
          } else {
            setStatus({
              message: "Identity validation did not return a session id",
              isError: true,
              isOkButtonVisible: true,
            });
            setIdentityStep(9);
            finishActivity();
          }
        } else if (identityStep === 2) {
          if (!sessionId) {
            setStatus({
              message: "Identity validation session id is missing",
              isError: true,
              isOkButtonVisible: true,
            });
            setIdentityStep(9);
            finishActivity();
            return;
          }

          let retryCount = 0;

          intervalRef.current = setInterval(() => {
            void (async () => {
              try {
                const response = await checkIdVerifyStatus(
                  (userInfo as any).okta?.id,
                  sessionId
                );

                if (response.error) {
                  setStatus({
                    title: "ERROR occurred!",
                    message: response.error,
                    isError: true,
                    isOkButtonVisible: true,
                  });
                  stopPolling();
                  setIdentityStep(9);
                  finishActivity();
                  return;
                }

                const data = response.data as any;
                const challengeResult = data.challenge_result;
                if (challengeResult === "PENDING") {
                  if (data.challenge_value !== null && data.challenge_value !== undefined) {
                    const message = `<span style="color: red;">Don't close this window</span><br>
                                        Please pass this code to ${(userInfo as any).firstName} to complete identity validation:<br>
                                        <b>${data.challenge_value}</b>`;
                    setStatus({ message, isError: false, isOkButtonVisible: false });
                  }
                } else if (challengeResult === "SUCCESS") {
                  stopPolling();
                  setStatus({
                    title: "Identity Validation Success!",
                    message: `${(userInfo as any).firstName}'s identity has been successfully validated!`,
                    isError: false,
                    isOkButtonVisible: true,
                  });
                  setIdentityStep(3);
                  setSessionId(null);
                  finishActivity();
                  return;
                } else {
                  stopPolling();
                  setStatus({
                    message: `Unknown status: ${challengeResult}`,
                    isError: true,
                    isOkButtonVisible: true,
                  });
                  setIdentityStep(9);
                  finishActivity();
                  return;
                }

                retryCount++;
                if (retryCount >= MAX_RETRY_COUNT) {
                  stopPolling();
                  setStatus({
                    title: "Timed out!",
                    message: "User validation timed out. Please try again later.",
                    isError: true,
                    isOkButtonVisible: true,
                  });
                  setIdentityStep(9);
                  finishActivity();
                }
              } catch (error) {
                stopPolling();
                setStatus({
                  title: "ERROR occurred!",
                  message: `Error checking identity validation status: ${error}`,
                  isError: true,
                  isOkButtonVisible: true,
                });
                setIdentityStep(9);
                finishActivity();
              }
            })();
          }, POLLING_INTERVAL);
        }
      } catch (error) {
        setStatus({
          title: "ERROR occurred!",
          message: `Error verifying the user: ${error}`,
          isError: true,
          isOkButtonVisible: true,
        });
        setIdentityStep(9);
        finishActivity();
      }
    };

    if ((identityStep === 1 || identityStep === 2) && !activityActiveRef.current) {
      beginActivity("identity");
      activityActiveRef.current = true;
    }

    if (identityStep !== 0) {
      validateIdentity();
    }

    return () => {
      stopPolling();
      finishActivity();
    };
  }, [identityStep, userInfo, sessionId]); // eslint-disable-line react-hooks/exhaustive-deps
};

export default useValidateIdentity;
